import React from 'react';
import { motion } from 'framer-motion';
import { AGENCY_HISTORY, TEAM_MEMBERS } from '../data/team';
import { Mail, Phone, ExternalLink, MapPin } from 'lucide-react';
import { AGENCY_INFO } from '../constants';

const About = () => {
    return (
        <div className="bg-white min-h-screen">
            {/* Hero Section with History */}
            <section className="relative py-20 overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-[#EA723D]/5 rounded-l-[100px] -z-10" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2 className="text-[#EA723D] font-bold tracking-wider uppercase text-sm mb-4">L'Agence</h2>
                            <h1 className="text-4xl md:text-5xl font-serif text-[#1A2A3A] mb-8 leading-tight">
                                {AGENCY_HISTORY.title}
                            </h1>
                            <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                                {AGENCY_HISTORY.content.map((paragraph, index) => (
                                    <p key={index}>{paragraph}</p>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="mt-12 lg:mt-0 relative"
                        >
                            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=800"
                                    alt="Notre Agence"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-gray-100 hidden md:block">
                                <p className="text-[#EA723D] font-bold text-3xl">20+</p>
                                <p className="text-gray-500 font-medium">Années d'expertise</p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-24 bg-[#1A2A3A]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-white text-4xl font-serif mb-4"
                        >
                            Rencontrez Notre Équipe
                        </motion.h2>
                        <div className="h-1 w-20 bg-[#EA723D] mx-auto rounded-full" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                        {TEAM_MEMBERS.map((member, index) => (
                            <motion.div
                                key={member.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
                            >
                                <div className="relative mb-6 overflow-hidden rounded-xl aspect-[3/4]">
                                    <img
                                        src={member.image}
                                        alt={member.name}
                                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A2A3A] to-transparent opacity-60" />
                                </div>
                                
                                <h3 className="text-2xl font-serif text-white mb-1">{member.name}</h3>
                                <p className="text-[#EA723D] font-medium mb-4 uppercase text-xs tracking-widest">{member.role}</p>
                                <p className="text-gray-400 text-sm leading-relaxed mb-6 italic">
                                    "{member.description}"
                                </p>

                                <div className="space-y-2 pt-4 border-t border-white/10">
                                    <a href={`tel:${member.phone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-white/70 hover:text-[#EA723D] transition-colors text-sm">
                                        <Phone size={14} /> {member.phone}
                                    </a>
                                    <a href={`mailto:${member.email}`} className="flex items-center gap-3 text-white/70 hover:text-[#EA723D] transition-colors text-sm">
                                        <Mail size={14} /> {member.email}
                                    </a>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values / Vision Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-[#EA723D]/5 rounded-3xl p-12 md:p-20 relative overflow-hidden">
                        <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl font-serif text-[#1A2A3A] mb-6">Notre Vision</h2>
                                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                    "Nous ne vendons pas des maisons, nous accompagnons des projets de vie. Notre réussite se mesure à la satisfaction de nos clients et à la pérennité des relations que nous tissons avec eux."
                                </p>
                                <div className="flex gap-4">
                                    <div className="flex flex-col">
                                        <span className="text-2xl font-bold text-[#EA723D]">98%</span>
                                        <span className="text-xs text-gray-500 uppercase tracking-tighter">Satisfaction clients</span>
                                    </div>
                                    <div className="w-px h-10 bg-gray-200" />
                                    <div className="flex flex-col">
                                        <span className="text-2xl font-bold text-[#EA723D]">500+</span>
                                        <span className="text-xs text-gray-500 uppercase tracking-tighter">Biens vendus</span>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="h-40 bg-[#1A2A3A] rounded-2xl flex items-center justify-center p-6 text-center">
                                    <p className="text-white font-serif italic font-light">Proximité</p>
                                </div>
                                <div className="h-40 bg-[#EA723D] rounded-2xl flex items-center justify-center p-6 text-center translate-y-8">
                                    <p className="text-white font-serif italic font-light">Transparence</p>
                                </div>
                                <div className="h-40 bg-[#EA723D] rounded-2xl flex items-center justify-center p-6 text-center">
                                    <p className="text-white font-serif italic font-light">Expertise</p>
                                </div>
                                <div className="h-40 bg-[#1A2A3A] rounded-2xl flex items-center justify-center p-6 text-center translate-y-8">
                                    <p className="text-white font-serif italic font-light">Innovation</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
